<!--
  FrameX right panel — reduced frame properties (Free tier).

  Shows only Frame Scale, Mark, Width, Height and Flag for the selected
  frame(s), for both single and multi selection. Mark/Width/Height/Flag reuse
  ItemProperties in `framex` mode — a single record, or a `makeMultiItem`
  aggregate whose differing fields read "Mixed". Frame Scale is owned here
  because it reads the frames' *location* clips (with Mixed handling across the
  selection); ItemProperties' item-drawing clip scale can't span a
  multi-selection (its item.id becomes an array). Ported from
  LocationProperties' clip-scale logic, scoped to the selected frame locations.
-->
<script>
  import cloneDeep from "lodash/cloneDeep";

  import ItemProperties from "#lib/sidebar/ItemProperties.svelte";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import SidebarSection from "#lib/sidebar/SidebarSection.svelte";
  import CustomScaleModal from "#lib/sidebar/CustomScaleModal.svelte";
  import { scaleOptions, definedMultipliers } from "@local/extensions/utilities/scales.js";
  import { locRecord } from "#src/extensions/filter-items.js";
  import makeMultiItem from "#src/extensions/multi-item.js";

  export let group;
  export let disabled = false;
  /** The selected frame locations (Document.svelte's `selectedLocationList`). */
  export let locations = [];
  export let types = [];
  export let collections = [];
  export let customColumns = [];
  export let standardColumns = {};

  let customScaleModal;

  $: records = locations.map((l) => $group.items[l.record_id]).filter(Boolean);
  $: single = records.length === 1;
  // makeMultiItem returns null for < 2 items, so use the record directly when
  // one frame is selected and the aggregate otherwise.
  $: item = single ? records[0] : makeMultiItem($group, records, customColumns);
  $: isCollection = single ? Boolean(records[0]?.is_collection) : locations[0]?.record_type === "collection";
  $: hasClip = locations.some((l) => l.clip);

  // Frame Scale: the frames' clip drawing scale, "Mixed" when they disagree.
  $: clipScale = frameScaleOf(locations);
  $: scaleValues = clipScaleOptions(clipScale);

  function frameScaleOf(locs) {
    const first = locs[0]?.clip?.drawing_scale ?? null;
    for (const l of locs) {
      if ((l.clip?.drawing_scale ?? null) !== first) return "Mixed";
    }
    return first;
  }

  function clipScaleOptions(scale) {
    if (scale === "Mixed") {
      return [
        { label: "Mixed", value: "Mixed" },
        { label: "None", value: null },
        { label: "Set Custom Scale", value: "custom", unclickable: true },
        ...scaleOptions,
      ];
    } else if (scale && !definedMultipliers[scale]) {
      return [
        { label: "None", value: null },
        { label: "Set Custom Scale", value: "custom", unclickable: true },
        ...scaleOptions,
        { label: `Custom Scale: ${scale}`, value: scale },
      ];
    }
    return [
      { label: "None", value: null },
      { label: "Set Custom Scale", value: "custom", unclickable: true },
      ...scaleOptions,
    ];
  }

  function updateFrameScale(e) {
    const value = e.detail.value;
    if (value === "Mixed") return;
    const seen = {};
    const updates = [];
    locations.forEach((loc) => {
      if (!loc.clip) return;
      const clip = { ...loc.clip, drawing_scale: value };
      updates.push({ type: "location", id: loc.id, diff: { clip } });
      const record = locRecord(loc, $group);
      if (record?.drawing?.attachment_id === loc.id && !seen[record.id]) {
        seen[record.id] = true;
        const drawing = cloneDeep(record.drawing);
        drawing.clip = clip;
        updates.push({
          type: loc.record_type === "collection" ? "item" : loc.record_type,
          id: record.id,
          diff: { drawing },
        });
      }
    });
    if (updates.length) group.update(updates);
  }

  function handleUpdateItem(e) {
    const { id, prop, value, diff } = e.detail;
    if (single) {
      group.updateItem(id, prop, value, diff);
    } else {
      group.updateMultipleItems(
        locations.map((l) => l.record_id),
        prop,
        value,
        diff,
      );
    }
  }
</script>

{#if item}
  <div class="fx-frame-props">
    <SidebarSection>
      <h3 class="px-2 mb-2">Properties</h3>
    </SidebarSection>
    <ItemProperties
      framex
      multiple={!single}
      {disabled}
      {group}
      {item}
      itemid={single ? item.id : null}
      {types}
      {collections}
      {customColumns}
      {standardColumns}
      {isCollection}
      settings={$group.data.settings}
      on:updateItem={handleUpdateItem} />
    {#if hasClip}
      <SidebarSection>
        <SelectInput
          {disabled}
          label="Frame Scale"
          labelWidth="7.5rem"
          value={clipScale}
          options={scaleValues}
          on:input={updateFrameScale}
          on:option-click={() => customScaleModal.open()} />
      </SidebarSection>
    {/if}
  </div>
{/if}

<CustomScaleModal bind:this={customScaleModal} on:setScale={updateFrameScale} />

<style lang="scss">
  // Set the Properties section off from the frames list above it: a full-width
  // rule directly abutting the list, then space before the "Properties"
  // heading. No space above the rule.
  .fx-frame-props {
    padding-top: 10px;
    border-top: 1px solid var(--fx-border-light, #eceef1);
  }
</style>
